import { useState, useCallback, type CSSProperties } from 'react'
import { Navigate } from 'react-router-dom'
import { api, ApiError } from '../../services/api'
import { useFeatures } from '../../hooks/use-features'
import { PageHeader, Alert, Modal, btnDanger, btnSecondary, card, colors, input, label } from '../../components/ui'

// The user must type this exact phrase to enable the destructive action.
const CONFIRM_PHRASE = 'please re-seed'

interface ReseedResponse {
  readonly message: string
  readonly durationMs: number
}

const warningCard: CSSProperties = {
  ...card,
  background: colors.bgError,
  border: `1px solid ${colors.borderError}`,
  marginTop: '1rem',
}

const warningHeading: CSSProperties = {
  margin: 0,
  fontSize: '0.9375rem',
  fontWeight: 600,
  color: colors.danger,
  marginBottom: '0.5rem',
}

const warningList: CSSProperties = {
  margin: 0,
  paddingLeft: '1.25rem',
  fontSize: '0.875rem',
  color: colors.text,
  lineHeight: 1.6,
}

const modalText: CSSProperties = {
  fontSize: '0.9375rem',
  lineHeight: 1.6,
  marginTop: 0,
  marginBottom: '0.75rem',
  color: colors.text,
}

const modalCode: CSSProperties = {
  fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
  background: colors.bgMuted,
  padding: '0.125rem 0.375rem',
  borderRadius: '3px',
  fontSize: '0.875rem',
  color: colors.danger,
}

const modalActions: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '0.75rem',
  marginTop: '1.25rem',
}

export function DemoPage() {
  const features = useFeatures()
  const [showConfirm, setShowConfirm] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [isReseeding, setIsReseeding] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const closeModal = useCallback(() => {
    if (isReseeding) return // prevent closing mid-operation
    setShowConfirm(false)
    setConfirmText('')
  }, [isReseeding])

  const openModal = useCallback(() => {
    setError(null)
    setSuccess(null)
    setConfirmText('')
    setShowConfirm(true)
  }, [])

  const handleReseed = useCallback(async () => {
    if (confirmText !== CONFIRM_PHRASE) return
    setIsReseeding(true)
    setError(null)
    setSuccess(null)
    try {
      const res = await api.post<ReseedResponse>('/demo/reseed', {})
      const seconds = res.data ? (res.data.durationMs / 1000).toFixed(1) : '?'
      setSuccess(`Database reseeded successfully in ${seconds}s. You may need to refresh to see new data.`)
      setShowConfirm(false)
      setConfirmText('')
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.status === 404
            ? 'Reseed endpoint is not enabled in this environment.'
            : err.message
          : err instanceof Error
            ? err.message
            : 'Failed to reseed database'
      setError(message)
    } finally {
      setIsReseeding(false)
    }
  }, [confirmText])

  const canConfirm = confirmText === CONFIRM_PHRASE && !isReseeding

  // Belt-and-braces: if the demo flag is off, this route isn't reachable
  // from the settings tabs, but someone with the URL could still hit it.
  // Redirect them back to the default settings view. Placed after the hooks
  // to satisfy the rules-of-hooks invariant.
  if (!features.demo) {
    return <Navigate to="/settings/providers" replace />
  }

  return (
    <div>
      <PageHeader title="Demo Controls" subtitle="Destructive operations for managing the public demo environment." />

      {error && <Alert type="error" message={error} onDismiss={() => setError(null)} />}
      {success && <Alert type="success" message={success} onDismiss={() => setSuccess(null)} />}

      <div style={warningCard}>
        <h2 style={warningHeading}>Re-seed demo database</h2>
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', color: colors.text }}>
          Wipes every record in the database and restores the original demo dataset. Use this to reset the environment
          between demo sessions.
        </p>
        <ul style={warningList}>
          <li>All patients, orders, results, and user modifications will be permanently lost.</li>
          <li>The original four seed users (admin, tech, lab_tech, viewer) will be restored.</li>
          <li>This action cannot be undone.</li>
        </ul>
        <div style={{ marginTop: '1rem' }}>
          <button type="button" style={btnDanger} onClick={openModal} data-testid="open-reseed-modal">
            Re-seed database
          </button>
        </div>
      </div>

      <Modal isOpen={showConfirm} onClose={closeModal} title="Confirm re-seed" width="480px">
        <p style={modalText}>
          This will <strong>permanently delete all data</strong> and restore the original demo dataset. There is no
          undo.
        </p>
        <p style={modalText}>
          To confirm, type <span style={modalCode}>{CONFIRM_PHRASE}</span> in the box below:
        </p>
        <label style={{ ...label, marginTop: '0.5rem' }} htmlFor="reseed-confirm-input">
          Confirmation phrase
        </label>
        <input
          id="reseed-confirm-input"
          type="text"
          autoComplete="off"
          autoFocus
          style={input}
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          disabled={isReseeding}
          placeholder={CONFIRM_PHRASE}
          data-testid="reseed-confirm-input"
        />
        <div style={modalActions}>
          <button
            type="button"
            style={btnSecondary}
            onClick={closeModal}
            disabled={isReseeding}
            data-testid="reseed-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            style={{ ...btnDanger, opacity: canConfirm ? 1 : 0.5 }}
            onClick={handleReseed}
            disabled={!canConfirm}
            data-testid="reseed-confirm"
          >
            {isReseeding ? 'Re-seeding...' : 'Re-seed database'}
          </button>
        </div>
      </Modal>
    </div>
  )
}
